import React, { useState } from "react";
import styled from "styled-components";
import { FaHeart, FaMapMarkerAlt } from "react-icons/fa";
import { IoEllipsisVertical } from "react-icons/io5";
import { SlFire } from "react-icons/sl";
import { IoFootsteps } from "react-icons/io5";
import threadmill from "../assets/threadmill.png";
import axios from "axios";
import { formatDateRange, formatDate } from "components/utils/DateUtils";
import openNotification from "components/utils/CustomNotification";
import { useNavigate } from "react-router-dom";
import SkeletonLoader from "components/Loaders/SkeletonLoader";

// --- Copied Helper Functions ---
export const ChallangeIcon = (challange: string) => {
  switch (challange) {
    case "steps":
      return <IoFootsteps />;
    case "distance":
      return <FaMapMarkerAlt />;
    case "caloriesburned":
      return <SlFire />;
    default:
      return null;
  }
};

export const ChallangeText = (challange: string) => {
  switch (challange) {
    case "steps":
      return "Steps";
    case "distance":
      return "Distance";
    case "caloriesburned":
      return "Calories burned";
    default:
      return null;
  }
};

export interface HomeChallenge {
  challenge_uid: string;
  challenge_name: string;
  challenge_desc: string;
  challenge_type: string;
  challenge_status: string;
  user_uid: string;
  group_uid: string;
  org_uid: string;
  metric_type: string;
  goal_count: number;
  goal_evaluation_mode?: string;
  group_name?: string;
  start_datetime?: string;
  end_datetime?: string;
  from_datetime?: string;
  to_datetime?: string;
  prize_pool: {
    total_amounts: string;
    distributions: { positions: string; amount: string; reward_type: string }[];
  };
  members_joined: number;
  image: string;
  joined?: boolean;
  join?: boolean;
  rejoin?: boolean;
  visibility?: string;
  last_registration_date?: string;
  image_details?: {
    image_uid: string;
    image?: string;
    timestamp?: string;
    default?: boolean;
  };
}

interface ChallangeProps {
  challengeData: HomeChallenge[];
  loading: boolean;
}

const Challenges: React.FC<ChallangeProps> = ({ challengeData, loading }) => {
  const [showMenuIndex, setShowMenuIndex] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] =
    useState<HomeChallenge | null>(null);
  const [quitReason, setQuitReason] = useState("");

  const navigate = useNavigate();

  // Helper to safely get dates
  const getStartDate = (ch: HomeChallenge) =>
    ch.start_datetime || ch.from_datetime;
  const getEndDate = (ch: HomeChallenge) => ch.end_datetime || ch.to_datetime;

  const handleJoinChallenge = async (challenge: HomeChallenge) => {
    const user_uid = localStorage.getItem("user_uid") || "";

    const payload = {
      challenge_uid: challenge.challenge_uid,
      user_uid: user_uid,
      org_uid: challenge.org_uid,
      is_rejoin: challenge.rejoin === true,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/join-challenge`,
        payload,
        {
          headers: {
            accept: "application/json",
            "user-uid": user_uid,
          },
          withCredentials: true,
        },
      );

      if (response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: response.data?.message,
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Failed to join challenge.",
        });
      }
    } catch (error) {
      openNotification({
        type: "error",
        message: "An error occurred while joining the challenge.",
      });
    }
  };

  return (
    <Container>
      {loading ? (
        <>
          {[...Array(2)].map((_, i) => (
            <SkeletonLoader key={i} type="challenge-card" />
          ))}
        </>
      ) : challengeData.length > 0 &&
        challengeData.some((ch) => ch.challenge_status === "active") ? (
        <>
          {challengeData
            .filter((challenge) => challenge.challenge_status === "active")
            .map((challenge, index) => (
              <Card
                key={challenge.challenge_uid}
                onClick={() => {
                  localStorage.setItem(
                    "selectedChallenge",
                    JSON.stringify(challenge),
                  );
                  navigate("/Challenges-dashboard", { state: challenge });
                }}
              >
                <ImageContainer>
                  <Image
                    src={challenge.image || threadmill}
                    alt={challenge.challenge_name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (
                        target.src !==
                        window.location.origin + "/fallback.png"
                      ) {
                        target.src = "fallback.png";
                      }
                    }}
                  />

                  <Overlay>
                    <TopRow>
                      <Metric>
                        <span style={{ marginRight: "8px" }}>
                          {ChallangeIcon(challenge.metric_type)}
                        </span>
                        {ChallangeText(challenge.metric_type)}
                      </Metric>

                      {/* Menu Button Logic - Only show if not rejoinable */}
                      {challenge.challenge_type === "individual" &&
                        !challenge.rejoin && (
                          <div style={{ position: "relative" }}>
                            <MenuButton
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showMenuIndex === index) {
                                  setShowMenuIndex(null);
                                } else {
                                  setShowMenuIndex(index);
                                }
                              }}
                            >
                              <IoEllipsisVertical />
                            </MenuButton>

                            {showMenuIndex === index && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  right: 0,
                                  background: "#fff",
                                  borderRadius: "6px",
                                  boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                                  zIndex: 999,
                                  overflow: "hidden",
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedChallenge(challenge);
                                    setQuitReason(""); // Reset reason on open
                                    setShowConfirmModal(true);
                                    setShowMenuIndex(null);
                                  }}
                                  style={{
                                    padding: "10px 16px",
                                    width: "100%",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#dc3545",
                                    fontSize: "14px",
                                    textAlign: "left",
                                    whiteSpace: "nowrap",
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.background =
                                      "#f8f9fa")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.background = "none")
                                  }
                                >
                                  Quit Challenge
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                    </TopRow>

                    <OverlayInfo>
                      <OverlayLeft>
                        <VisibilityBadge>
                          {challenge.visibility === "private"
                            ? "Private"
                            : "Public"}
                        </VisibilityBadge>
                      </OverlayLeft>

                      <OverlayRight>
                        {/* MOVED JOIN BUTTON HERE - from TopRow to OverlayRight */}
                        {(challenge.join || challenge.rejoin) &&
                          challenge.visibility === "public" && (
                            <JoinButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleJoinChallenge(challenge);
                              }}
                              style={{ marginBottom: "8px" }}
                            >
                              {challenge.join
                                ? "Join"
                                : challenge.rejoin
                                  ? "Rejoin"
                                  : null}
                            </JoinButton>
                          )}

                        <Titlename>{challenge.challenge_name}</Titlename>
                        <Subtitle>
                          {(() => {
                            let metricDisplay = ChallangeText(
                              challenge.metric_type,
                            );
                            if (challenge.metric_type === "distance") {
                              metricDisplay = "meters";
                            } else if (
                              challenge.metric_type === "caloriesburned"
                            ) {
                              metricDisplay = "kcal";
                            }

                            const goalText = `${challenge.goal_count} ${metricDisplay}`;
                            const evaluationText =
                              challenge.goal_evaluation_mode === "daily"
                                ? " per day"
                                : "";
                            return `Goal: ${goalText}${evaluationText}`;
                          })()}
                        </Subtitle>
                        <StyledDate>
                          {formatDateRange(
                            getStartDate(challenge),
                            getEndDate(challenge),
                          )}
                        </StyledDate>
                        {challenge.challenge_status === "inactive" ||
                          (challenge.challenge_status === "active" &&
                            challenge.last_registration_date && (
                              <StatusTag variant="warning">
                                Last Registration:{" "}
                                {formatDate(challenge.last_registration_date)}
                              </StatusTag>
                            ))}
                      </OverlayRight>
                    </OverlayInfo>
                  </Overlay>
                </ImageContainer>

                <Info>
                  <Title>{challenge.challenge_desc}</Title>
                </Info>

                <BottomRow>
                  <JoinedInfo>
                    <LeftInfo>
                      <JoinedText>
                        <FaHeart /> {challenge.members_joined} joined
                      </JoinedText>
                    </LeftInfo>
                    <RightInfo>
                      <JoinedText1>
                        {challenge.challenge_type === "team"
                          ? "Team"
                          : "Individual"}
                      </JoinedText1>
                    </RightInfo>
                  </JoinedInfo>
                </BottomRow>
              </Card>
            ))}

          {/* CTA Card for odd number of challenges */}
          {challengeData.filter((ch) => ch.challenge_status === "active")
            .length %
            2 !==
            0 && (
            <CTACard onClick={() => navigate("/challenges")}>
              <p>Join new challenges and compete!</p>
              <button>Browse Challenges</button>
            </CTACard>
          )}
        </>
      ) : (
        <div className="home-challenges-no-data-condition">
          <h3 style={{ marginBottom: "12px", color: "#333" }}>
            No Joined Challenges Yet
          </h3>
          <p
            style={{
              fontSize: "0.95rem",
              color: "var(--text-color)",
              marginBottom: "20px",
            }}
          >
            Explore and join exciting fitness challenges to get started!
          </p>
          <button
            style={{
              padding: "10px 20px",
              backgroundColor: "#094752",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => navigate("/challenges")}
          >
            Browse Challenges
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedChallenge && (
        <ModalOverlay onClick={() => setShowConfirmModal(false)}>
          <QuitModalContainer onClick={(e) => e.stopPropagation()}>
            <QuitModalHeader>
              <WarningIcon>⚠️</WarningIcon>
              <QuitModalTitle>Quit Challenge</QuitModalTitle>
            </QuitModalHeader>
            <p style={{ color: "var(--text-color)", marginBottom: "16px" }}>
              Are you sure you want to quit "
              <strong>{selectedChallenge.challenge_name}</strong>"?
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "var(--text-color)",
                }}
              >
                Reason for quitting <span style={{ color: "red" }}>*</span>
              </label>
              <QuitReasonTextarea
                placeholder="Please tell us why you are leaving..."
                value={quitReason}
                onChange={(e) => setQuitReason(e.target.value)}
                maxLength={100}
              />
              <div
                style={{
                  textAlign: "right",
                  fontSize: "12px",
                  color: "#999",
                  marginTop: "4px",
                }}
              >
                {quitReason.length}/100
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <CancelButton onClick={() => setShowConfirmModal(false)}>
                Cancel
              </CancelButton>
              <ConfirmQuitButton
                disabled={!quitReason.trim()}
                style={{
                  opacity: !quitReason.trim() ? 0.6 : 1,
                  cursor: !quitReason.trim() ? "not-allowed" : "pointer",
                }}
                onClick={async () => {
                  if (!selectedChallenge) return;
                  if (!quitReason.trim()) {
                    openNotification({
                      type: "warning",
                      message: "Please provide a reason",
                    });
                    return;
                  }

                  const user_uid = localStorage.getItem("user_uid") || "";
                  const startDt = getStartDate(selectedChallenge);

                  const payload = {
                    challenge_uid: selectedChallenge.challenge_uid,
                    org_uid: selectedChallenge.org_uid,
                    user_uid: user_uid,
                    start_datetime: startDt,
                    reason: quitReason,
                  };

                  try {
                    const response = await axios.delete(
                      `${process.env.REACT_APP_API_URL}/api/quit-challenge`,
                      {
                        headers: {
                          accept: "application/json",
                          "user-uid": user_uid,
                        },
                        withCredentials: true,
                        data: payload,
                      },
                    );

                    if (response.data?.status === "Success") {
                      openNotification({
                        type: "success",
                        message: response.data?.message,
                      });
                      setTimeout(() => {
                        window.location.reload();
                      }, 1000);
                    } else {
                      openNotification({
                        type: "error",
                        message:
                          response.data?.message || "Failed to leave Challenge",
                      });
                    }
                  } catch (error) {
                    openNotification({
                      type: "error",
                      message: "Error occurred while quitting",
                    });
                  }
                  setShowConfirmModal(false);
                }}
              >
                Yes, Quit
              </ConfirmQuitButton>
            </div>
          </QuitModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Challenges;

// --- STYLED COMPONENTS ---

const Container = styled.div`
  display: grid;
  background: var(--card-background);
  border-radius: 10px;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 16px;

  .home-challenges-no-data-condition {
    font-size: 0.875rem;
    text-align: center;
    grid-column: 1 / -1;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--card-background);
  border-radius: 10px;
  cursor: pointer;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  border-radius: 10px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(89, 87, 87, 0.2);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  color: white;
`;

const OverlayInfo = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const OverlayLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  max-width: 50%;
`;

const OverlayRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  max-width: 80%;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const Metric = styled.span`
  display: flex;
  align-items: center;
  background: var(--card-background);
  color: var(--text-color);
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: bold;

  @media (max-width: 1024px) {
    padding: 5px 9px;
    border-radius: 8px;
    font-size: 12px;
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  position: relative;
`;

const JoinButton = styled.button`
  padding: 6px 12px;
  background: #00a86b;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s;
  white-space: nowrap;

  &:hover {
    background: #007a52;
  }

  @media (max-width: 1024px) {
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 0.8rem;
  }
  @media (max-width: 760px) {
    padding: 6px 12px;
    border-radius: 12px;
    font-size: 0.875rem;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--card-background);
  padding: 8px 0;
`;

const Title = styled.h2`
  font-size: 1rem;
  color: var(--text-color);
  font-weight: 500;
  margin: 0;
`;

const Titlename = styled.span`
  font-size: 1.3rem;
  font-weight: 400;
  color: white;
  @media (max-width: 1024px) {
    font-size: 1.15rem;
  }
  @media (max-width: 760px) {
    font-size: 1.3rem;
  }
`;

const Subtitle = styled.span`
  font-size: 0.875rem;
  color: white;
`;

const StyledDate = styled.span`
  font-size: 0.875rem;
  color: white;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 8px 12px;
  background: var(--card-background);
`;

const JoinedInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  white-space: nowrap;
`;

const LeftInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RightInfo = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const JoinedText = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: red;
  font-weight: bold;
`;

const JoinedText1 = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-color);
  font-weight: bold;
`;

const Badge = styled.div`
  background: #28a745;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  color: white;
  font-weight: bold;
`;

const VisibilityBadge = styled.div`
  background: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #28a745;
  margin-top: 6px;
  display: inline-block;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatusTag = styled.div<{ variant?: "warning" | "success" | "info" }>`
  display: inline-block;
  margin-top: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${(props) =>
    props.variant === "warning" ? "#fff3cd" : "#d1ecf1"};
  color: ${(props) => (props.variant === "warning" ? "#856404" : "#0c5460")};
`;

const CTACard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border: 2px dashed #094752;
  border-radius: 10px;
  padding: 32px 16px;
  min-height: 250px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #e8e8e8 0%, #d8d8d8 100%);
    transform: translateY(-2px);
  }

  p {
    color: #666;
    font-size: 14px;
    margin-bottom: 16px;
  }

  button {
    padding: 10px 20px;
    background-color: #094752;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }
`;

/* --- Modal Styles --- */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  backdrop-filter: blur(4px);
`;

const QuitModalContainer = styled.div`
  background: var(--card-background);
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const QuitModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const WarningIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #fff3cd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const QuitModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #e5e7eb;
  }
`;

const ConfirmQuitButton = styled.button`
  padding: 10px 24px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #c82333;
  }
`;

const QuitReasonTextarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  resize: none;
  height: 80px;
  font-family: inherit;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #094752;
  }
`;
